import { ListItemButton } from '@mui/material';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { LegStatus } from '@api/Trinium/types';
import { batchLegLabel, StatusActionProps } from '@components/DataGrid/Dispatch/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

/**
 * Pickup Now Action List Item
 * */
export default function PickupAction({ leg, onError, selectedRows }: StatusActionProps) {
	const appDispatch = useAppDispatch();
	const { number: terminalNumber } = useCurrentTerminal();
	const [putLegs] = usePutLegsMutation();

	/**
	 * This action requires Leg to have no actual pickup date (LegStatus of AV or AC)
	 * */
	if (![LegStatus.AV, LegStatus.AC].includes(getEnumValueByKey(LegStatus, leg.legStatus) as LegStatus)) {
		return <></>;
	}
	const canPickup = (row: DispatchLeg) =>
		[LegStatus.AV, LegStatus.AC].includes(getEnumValueByKey(LegStatus, row.legStatus) as LegStatus) && Boolean(row.drvId);

	const candidateRows = selectedRows?.some((selectedRow) => selectedRow.legId === leg.legId) ? selectedRows : [leg];
	const targetRows = candidateRows.filter(canPickup);
	/** The clicked leg gates the action so the row the dispatcher is looking at is never silently skipped */
	const isDisabled = !canPickup(leg);

	const handleBtnClick = () => {
		void putLegs({
			terminalNumber,
			actionType: {
				action: 'pickup',
				data: {
					legIds: targetRows.map((targetRow) => targetRow.legId),
					pickupDate: dayjsNow().format('YYYY-MM-DDTHH:mm:00.000+00:00')
				}
			}
		})
			.unwrap()
			.then((response) => {
				response.errors.forEach((error) => {
					if (onError) onError(error.errorMessage);
					else appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
				});
			})
			.catch((error: unknown) => {
				const errorMessage = getApiErrorMessage(error);
				if (onError) onError(errorMessage);
				else appDispatch(appendErrorAnnouncement({ message: errorMessage }));
			});
	};

	return (
		<DisabledTooltip message={isDisabled ? 'Driver not currently assigned' : undefined}>
			<ListItemButton
				onClick={handleBtnClick}
				disabled={isDisabled}
			>
				{batchLegLabel('Pickup Now', targetRows.length)}
			</ListItemButton>
		</DisabledTooltip>
	);
}
